<template>
  <div class="ModifyInsuranceModal">
    <v-form
      ref="$form"
      validate-on="input lazy"
    >
      <ForWhomSelectorComponent
        v-model:forWhom="forWhom"
        v-model:forWhomPeople="forWhomPeople"
      />

      <!-- Tipo de seguro -->
      <BaseSelect
        v-if="isModify"
        id="insurance-type"
        :label="t('health-foreign.insurance-type.legend')"
        :value="newAnswers.insuranceType"
        :items="insuranceTypes"
        v-model="newAnswers.insuranceType"
      />

      <!-- Fecha de inicio -->
      <DatePickerComponent
        id="start-date"
        :label="t('rater.common.modify-search-view.start-date')"
        v-model="newAnswers.startDate"
        :datePickerVisible="!isModify"
        :min-date="minDateInit"
        :max-date="maxDateInit"
        :rules="[required]"
      />

      <DatePickerComponent
        id="end-date"
        v-if="newAnswers.insuranceType === props.configurations.insuranceTypes.student.id"
        :label="t('rater.common.modify-search-view.end-date')"
        v-model="newAnswers.endDate"
        :datePickerVisible="!isModify"
        :min-date="minDateEnd"
        :max-date="maxDateEnd"
        :rules="[required, validateEndDate]"
      />

      <InsuredInfoComponent
        :key="insuredKey + newAnswers.startDate"
        ref="$insuredInfoComponent"
        :answers="newAnswers"
        :configurations="configurations"
        :useTooOldGreaterOrEqual="true"
        :showTitle="false"
        :cp-validation-rules="cpValidationRules"
      />

      <div class="actions">
        <BaseButton @click="submit">
          {{ t(`rater.base.${isModify ? 'recalculate-submit' : 'submit'}`) }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
  import InsuredInfoComponent from '@/modules/common/common/components/panels/InsuredInfoComponent.vue'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import ForWhomSelectorComponent from '@/modules/core/components/formFields/ForWhomSelectorComponent.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import ForeignHealthConfigurations from '@/modules/foreign-health/common/models/APP/ForeignHealthConfigurations'
  import ForeignHealthInsured from '@/modules/foreign-health/hiring/models/MID/ForeignHealthInsured'
  import { addDaysToDate, addMonthsToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ForeignHealthRaterAnswers from '../../models/APP/ForeignHealthRaterAnswers'

  const { t } = useI18n()
  const { required, isNotFromNavarra } = useRules()

  const emit = defineEmits(['submit', 'close'])

  interface PropsInterface {
    answers: ForeignHealthRaterAnswers
    configurations: ForeignHealthConfigurations
    isModify?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), { isModify: true })
  const $form = ref()
  const newAnswers = ref(new ForeignHealthRaterAnswers(props.answers))
  const forWhom = ref(newAnswers.value.forWhom)
  const forWhomPeople = ref(newAnswers.value.forWhomPeople)

  const insuranceTypes = computed(() => props.configurations.insuranceTypesOptions)
  const insuredKey = computed(() => forWhom.value ?? '' + forWhomPeople.value)
  const $insuredInfoComponent = ref()

  const minDateInit = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })
  const maxDateInit = computed(() =>
    props.configurations.maxDays ? addDaysToDate(props.configurations.maxDays) : null
  )

  const minDateEnd = computed(() => calculateMaxDate(newAnswers.value.startDate, 'min'))
  const maxDateEnd = computed(() => calculateMaxDate(newAnswers.value.startDate, 'max'))

  function calculateMaxDate(startDate: Date | null, dateOption: 'min' | 'max'): Date {
    let innerStartDate = startDate

    if (!innerStartDate) {
      innerStartDate = new Date()
      innerStartDate.setHours(0, 0, 0, 0)
    }

    if (!props.configurations.hiringEndDateStudent) {
      return innerStartDate
    }
    if (props.configurations.hiringEndDateStudent.time === 'month') {
      return addMonthsToDate(
        Number(props.configurations.hiringEndDateStudent[dateOption]),
        addDaysToDate(-1, innerStartDate)
      )
    }
    return addDaysToDate(
      Number(props.configurations.hiringEndDateStudent[dateOption]),
      innerStartDate
    )
  }

  const cpValidationRules = [isNotFromNavarra]

  onBeforeMount(() => {
    newAnswers.value.startDate = props.answers.startDate ?? addDaysToDate(1)
    newAnswers.value.insuranceType =
      props.answers.insuranceType ?? props.configurations.defaultInsuranceType
  })

  watch(forWhom, (newForWhom) => {
    if (newForWhom === ForWhomEnum.NOT_ME && newAnswers.value.insureds.length > 0) {
      newAnswers.value.insureds[0].role = UserRoleEnum.INSURED
    }
    if (newForWhom !== ForWhomEnum.NOT_ME && newAnswers.value.insureds.length > 0) {
      newAnswers.value.insureds[0].role = UserRoleEnum.HOLDER_INSURED
    }
    newAnswers.value.forWhom = newForWhom
  })
  watch(forWhomPeople, (newForWhomPeople, prevForWhomPeople) => {
    if (newForWhomPeople > prevForWhomPeople) {
      for (let i = prevForWhomPeople; i < newForWhomPeople; i++) {
        newAnswers.value.insureds.push(
          new ForeignHealthInsured({
            role: UserRoleEnum.INSURED,
            riskNumber: i,
            addressDetails: new AddressDetails(newAnswers.value.insureds[0].addressDetails)
          })
        )
      }
    }
    if (newForWhomPeople < prevForWhomPeople) {
      newAnswers.value.insureds = newAnswers.value.insureds.slice(
        0,
        newForWhomPeople - prevForWhomPeople
      )
    }
    newAnswers.value.forWhomPeople = newForWhomPeople
  })

  function getStayAbroadType(insuranceTypeValue: string) {
    if (insuranceTypeValue === props.configurations.insuranceTypes.student.id) {
      return '3'
    }
    if (insuranceTypeValue === props.configurations.insuranceTypes.resident.id) {
      return '4'
    }
    return ''
  }

  //TODO: Move to validation composable
  function validateEndDate(value: Date | string | null): true | string {
    if (!newAnswers.value.startDate || !value) {
      return t('rater.health-foreign.duration.min-range-error')
    }

    // Convertir el valor a un formato válido para Date
    const parsedValue = typeof value === 'string' ? value.split('/').reverse().join('-') : value
    const endDate = parsedValue instanceof Date ? parsedValue : new Date(parsedValue)

    if (isNaN(endDate.getTime())) {
      return t('rater.health-foreign.duration.min-range-error')
    }
    endDate.setHours(0, 0, 0, 0)

    const minEnd = calculateMaxDate(newAnswers.value.startDate, 'min')
    const maxEnd = calculateMaxDate(newAnswers.value.startDate, 'max')
    if (endDate < minEnd) {
      return t('rater.health-foreign.duration.min-range-error')
    }
    if (endDate > maxEnd) {
      return t('rater.health-foreign.duration.max-range-error')
    }
    return true
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return

    const { insureds } = $insuredInfoComponent.value!.submit()

    newAnswers.value.updateAttributes({ insureds })
    if (newAnswers.value.insuranceType !== props.configurations.insuranceTypes.student.id) {
      newAnswers.value.endDate = null
    }
    newAnswers.value.updateInsuredAttributes(0, insureds[0])
    newAnswers.value.stayAbroadType = getStayAbroadType(newAnswers.value.insuranceType)

    emit('submit', newAnswers.value)
  }
</script>

<style lang="scss" scoped>
  .ModifyInsuranceModal {
    max-width: $size-350;
    margin: $spacing-l auto;
    width: $size-full;

    .actions {
      margin-top: $spacing-2;
      padding-bottom: $spacing-2xl;
    }
  }
</style>
